/**
 * A tab strip that overflows (v1's `wheelScroll.js`): the mouse wheel moves it
 * sideways, and `scrollLeft` / `scrollRight` say there is more past an edge,
 * for the fade. A trackpad's own sideways swipe is left alone.
 */
import { useEffect, useRef, useState } from "react";

export function useTabScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const update = () => {
      const can = node.scrollWidth > node.clientWidth + 1;
      setEdges({
        left: can && node.scrollLeft > 1,
        right: can && node.scrollLeft < node.scrollWidth - node.clientWidth - 1,
      });
    };
    const onWheel = (event: WheelEvent) => {
      if (node.scrollWidth <= node.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      node.scrollLeft += event.deltaY;
      event.preventDefault();
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    node.addEventListener("scroll", update, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(node);
    update();
    return () => {
      node.removeEventListener("wheel", onWheel);
      node.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, []);

  return { ref, edges };
}
